import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../common/utils';
import * as Icon from '../../components/icons';
import { Container } from '../../components/layout/ContainerFlex';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';
import { StatListItem } from '../../components/stats/StatListItem';
import { UOMMetrics } from '../types/metrics';

interface InventoryMetrics {
  totalSKUs: number;
  activeSKUs: number;
  stockUtilization: number;
  stockTurnover: number;
  avgDaysOnHand: number;
  stockAccuracy: number;
  lowStockItems: number;
  outOfStockItems: number;
  excessStockValue: number;
}

interface WarehouseDashboardInventoryProps {
  inventoryMetrics: InventoryMetrics;
  uomMetrics: UOMMetrics[];
  timeRange: string;
}

export const WarehouseDashboardInventory: React.FC<
  WarehouseDashboardInventoryProps
> = ({ inventoryMetrics, uomMetrics, timeRange }) => {
  const { t } = useTranslation('app');

  return (
    <Container col hasOverflowY className="gap-1">
      <StatGroup
        title={t('Inventory Dashboard')}
        classNameStatGroupItems={cn(
          `!my-0 !space-y-0 grid gap-1`,
          `grid-cols-${Math.min(3, 3)}`,
          `md:grid-cols-${Math.min(3, 3)}`,
          `lg:grid-cols-${Math.min(3, 3)}`,
        )}
        className="!my-0"
      >
        {/* Inventory Overview */}
        <Stat
          hasHelper
          isPreview
          title={t('Inventory Overview')}
          value={inventoryMetrics.totalSKUs}
          unitOfMeasure="SKUs"
          icon={Icon.Box}
        >
          <StatListItem
            title={t('Active SKUs')}
            value={inventoryMetrics.activeSKUs}
          />
          <StatListItem
            title={t('Stock Utilization')}
            value={inventoryMetrics.stockUtilization}
            unitOfMeasure="%"
          />
          <StatListItem
            title={t('Stock Turnover')}
            value={inventoryMetrics.stockTurnover}
            unitOfMeasure="x"
          />
        </Stat>

        {/* Stock Health */}
        <Stat
          hasHelper
          isPreview
          title={t('Stock Health')}
          value={inventoryMetrics.stockAccuracy}
          unitOfMeasure="%"
          icon={Icon.AlertTriangle}
        >
          <StatListItem
            title={t('Avg Days on Hand')}
            value={inventoryMetrics.avgDaysOnHand}
          />
          <StatListItem
            title={t('Low Stock Items')}
            value={inventoryMetrics.lowStockItems}
          />
          <StatListItem
            title={t('Out of Stock')}
            value={inventoryMetrics.outOfStockItems}
          />
          <StatListItem
            title={t('Excess Stock Value')}
            value={inventoryMetrics.excessStockValue}
            unitOfMeasure="$"
            isUnitPrefix
          />
        </Stat>

        {/* UOM Performance */}
        <Stat
          hasHelper
          isPreview
          title={t('UOM Performance')}
          value={uomMetrics[0]?.unitsPerHour || 0}
          unitOfMeasure="units/hr"
          icon={Icon.ChartBarSquare}
        >
          {uomMetrics.map((uom, index) => (
            <React.Fragment key={index}>
              <StatListItem
                title={uom.uomType}
                value={uom.unitsPerHour}
                unitOfMeasure="units/hr"
              />
              <StatListItem
                title={t('Total Units')}
                value={uom.totalUnits}
                className="pl-4"
              />
              <StatListItem
                title={t('Handling Time')}
                value={uom.avgHandlingTime}
                unitOfMeasure="s"
                className="pl-4"
              />
            </React.Fragment>
          ))}
        </Stat>
      </StatGroup>
    </Container>
  );
};

export default WarehouseDashboardInventory;
